import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './apiClient';
import type { ReportFormat } from './reports';

export interface BoardPackDistribution {
  id: string;
  boardPackVersionId: string;
  versionNo: number;
  recipientUserId: string;
  recipientName: string;
  recipientEmail: string | null;
  distributedBy: string;
  distributedDate: string;
  note: string | null;
  firstViewedDate: string | null;
  viewCount: number;
  downloadCount: number;
  acknowledgedDate: string | null;
  revokedDate: string | null;
  revokedBy: string | null;
  revocationReason: string | null;
}

export interface MyBoardPack {
  distributionId: string;
  boardPackId: string;
  title: string;
  audience: string | null;
  periodName: string | null;
  versionNo: number;
  publishedDate: string;
  distributedBy: string;
  distributedDate: string;
  note: string | null;
  firstViewedDate: string | null;
  acknowledgedDate: string | null;
  /** A re-issue (ToR 2.1(iii)): what changed, the sections that differ from the version before, that version; and where the reader last was (JSON {page, key}). */
  changeNote: string | null;
  changedKeys: string[] | null;
  previousVersionNo: number | null;
  lastReadPosition: string | null;
}

export interface RecipientOption {
  userId: string;
  fullName: string;
  email: string | null;
  jobTitle: string | null;
}

/** The meeting's attendance register matched to user accounts — who the pack goes to unless told otherwise. */
export interface DefaultRecipients {
  recipients: RecipientOption[];
  unmatched: string[];
  meetingTitle: string | null;
}

export interface MyBoardPackPaper {
  code: string;
  title: string;
  documentId: string;
  versionNo: number;
  fileName: string;
  sizeBytes: number;
  /** Read in the reading room only: no download, and left out of downloads of the pack. */
  viewOnly: boolean;
}

/** Fetches a paper for reading in the app (the only way a view-only paper is served; watermarked). */
export async function fetchMyBoardPackPaperBlob(distributionId: string, paper: MyBoardPackPaper): Promise<Blob> {
  return (await apiClient.get(`/api/v1/my-board-packs/${distributionId}/papers/${paper.documentId}/download`, {
    params: { version: paper.versionNo, inline: true }, responseType: 'blob',
  })).data as Blob;
}

export interface MyBoardPackSection {
  /** The section's position in the issued pack: what a note anchors to. */
  number: number;
  /** What it prints under — "2", or "2.1" for a sub-section. */
  label: string | null;
  /** 0 for a section, 1 for a sub-section. */
  level: number;
  heading: string;
  purpose: string | null;
  papers: MyBoardPackPaper[];
  /** A short generated summary of the section, made at publish and frozen with it. */
  brief: string | null;
}

export interface PackAnswerSource { code: string; title: string; excerpt: string }
export interface PackAnswer {
  question: string;
  answer: string;
  generatorName: string;
  generatedExternally: boolean;
  /** The passages the answer rests on, by section number or paper code. */
  sources: PackAnswerSource[];
}

/** Asks the issued pack a question; the answer is composed from the pack's own passages, within what the recipient may see. */
export async function askMyBoardPack(distributionId: string, question: string): Promise<PackAnswer> {
  return (await apiClient.post<PackAnswer>(`/api/v1/my-board-packs/${distributionId}/ask`, { question })).data;
}

export interface MyBoardPackContents {
  title: string;
  versionNo: number;
  meetingTitle: string | null;
  sections: MyBoardPackSection[];
  /** What the recipient's place on the meeting's register allows (ToR 4.1.7). */
  canDownload: boolean;
  canPrint: boolean;
}

export function useDefaultRecipients(boardPackId: string | undefined) {
  return useQuery({
    queryKey: ['boardPack', boardPackId, 'default-recipients'],
    queryFn: async () => (await apiClient.get<DefaultRecipients>(`/api/v1/boardpacks/${boardPackId}/default-recipients`)).data,
    enabled: Boolean(boardPackId),
  });
}

export type AnnotationVisibility = 'Private' | 'Recipients' | 'Selected';

/** What a mark on the pack is (ToR 2.1(ii)). */
export type AnnotationKind = 'Note' | 'Highlight' | 'Underline' | 'Ink' | 'Sticky';
/** The colours a mark may take in the reader. */
export const MARK_COLOURS = ['#FFEB3B', '#8BC34A', '#4FC3F7', '#F48FB1', '#FF8A65'];

/** Where a note was made: typed in the app, or a comment in ONLYOFFICE on the recipient's copy of a paper. */
export type AnnotationSource = 'App' | 'OnlyOffice';

/** Where a note's passage is in the issued PDF: a page and rectangles as fractions of it. */
export interface AnnotationAnchor { page: number; rects: { x: number; y: number; w: number; h: number }[] }

export interface BoardPackAnnotation {
  id: string;
  sectionNumber: number;
  sectionHeading: string;
  paperDocumentId: string | null;
  text: string;
  visibility: AnnotationVisibility;
  sharedWith: { userId: string; name: string }[];
  authorName: string;
  /** Only the author edits, deletes or re-shares a note. */
  isMine: boolean;
  createdDate: string;
  modifiedDate: string | null;
  /** Also raised with the secretariat, whatever the visibility says about other members. */
  forSecretariat: boolean;
  source: AnnotationSource;
  /** The passage the note is about, when it was made on a selection or on a passage in ONLYOFFICE. */
  quote: string | null;
  /** JSON of an AnnotationAnchor; parse with parseAnchor. */
  anchor: string | null;
  page: number | null;
  /** What the mark is, its shape (ink strokes or a sticky note's pin, as fractions of the page) and its colour. */
  kind: AnnotationKind;
  shapeJson: string | null;
  colour: string | null;
  parentAnnotationId: string | null;
  /** Answers, oldest first. A reply is read by whoever reads its parent and has no visibility of its own. */
  replies: BoardPackAnnotation[];
}

export function parseAnchor(anchor: string | null): AnnotationAnchor | null {
  if (!anchor) return null;
  try {
    const a = JSON.parse(anchor) as Partial<AnnotationAnchor>;
    return typeof a.page === 'number' && a.page > 0 ? { page: a.page, rects: Array.isArray(a.rects) ? a.rects : [] } : null;
  } catch {
    return null;
  }
}

export interface AnnotationRecipient {
  userId: string;
  name: string;
}

export { mentionHandle } from './onlyOffice';

/** Tells the API who was @mentioned in a comment on the recipient's copy of a paper; the API notifies them. */
export async function notifyPaperMentions(distributionId: string, documentId: string, handles: string[], message: string | null): Promise<void> {
  await apiClient.post(`/api/v1/my-board-packs/${distributionId}/papers/${documentId}/mentions`, { handles, message });
}

/** A note a member raised with the secretariat, with its answers. */
export interface MembersNote {
  id: string;
  sectionNumber: number;
  sectionHeading: string;
  paperDocumentId: string | null;
  authorName: string;
  text: string;
  quote: string | null;
  page: number | null;
  source: AnnotationSource;
  createdDate: string;
  replies: MembersNote[];
}

/** The notes members flagged for the secretariat on an issued version (the latest when none is given). Needs BoardPacks.Manage. */
export function useBoardPackMembersNotes(boardPackId: string | undefined, versionNo?: number) {
  return useQuery({
    queryKey: ['boardPacks', boardPackId, 'members-notes', versionNo ?? 'latest'],
    enabled: Boolean(boardPackId),
    queryFn: async () => (await apiClient.get<MembersNote[]>(`/api/v1/boardpacks/${boardPackId}/members-notes`, { params: versionNo ? { version: versionNo } : {} })).data,
  });
}

/** The other people this version was issued to — who a note can be shared with. */
export function useMyBoardPackCoRecipients(distributionId: string | null) {
  return useQuery({
    queryKey: ['myBoardPacks', distributionId, 'co-recipients'],
    queryFn: async () => (await apiClient.get<AnnotationRecipient[]>(`/api/v1/my-board-packs/${distributionId}/co-recipients`)).data,
    enabled: Boolean(distributionId),
  });
}

/** The recipient's private notes on an issued pack. */
export function useMyBoardPackAnnotations(distributionId: string | null) {
  return useQuery({
    queryKey: ['myBoardPacks', distributionId, 'annotations'],
    queryFn: async () => (await apiClient.get<BoardPackAnnotation[]>(`/api/v1/my-board-packs/${distributionId}/annotations`)).data,
    enabled: Boolean(distributionId),
  });
}

export function useMyBoardPackAnnotationMutations(distributionId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['myBoardPacks', distributionId, 'annotations'] });
  const add = useMutation({
    mutationFn: async (input: {
      sectionNumber: number; paperDocumentId: string | null; text: string; visibility: AnnotationVisibility; sharedWithUserIds: string[] | null;
      quote?: string | null; anchor?: string | null; parentAnnotationId?: string | null; forSecretariat?: boolean;
      kind?: AnnotationKind; shapeJson?: string | null; colour?: string | null; clientId?: string;
    }) =>
      (await apiClient.post<BoardPackAnnotation>(`/api/v1/my-board-packs/${distributionId}/annotations`, input)).data,
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ id, text, visibility, sharedWithUserIds, forSecretariat, colour, shapeJson }: {
      id: string; text: string; visibility?: AnnotationVisibility; sharedWithUserIds?: string[] | null; forSecretariat?: boolean; colour?: string | null; shapeJson?: string | null;
    }) =>
      (await apiClient.put<BoardPackAnnotation>(`/api/v1/my-board-packs/${distributionId}/annotations/${id}`, { text, visibility, sharedWithUserIds, forSecretariat, colour, shapeJson })).data,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/api/v1/my-board-packs/${distributionId}/annotations/${id}`); },
    onSuccess: invalidate,
  });
  return { add, update, remove };
}

export function useMyBoardPackContents(distributionId: string | null) {
  return useQuery({
    queryKey: ['myBoardPacks', distributionId, 'contents'],
    queryFn: async () => (await apiClient.get<MyBoardPackContents>(`/api/v1/my-board-packs/${distributionId}/contents`)).data,
    enabled: Boolean(distributionId),
  });
}

/** Opens one paper of an issued pack, at the version the pack froze. */
export async function downloadMyBoardPackPaper(distributionId: string, paper: MyBoardPackPaper) {
  const blob = (await apiClient.get(`/api/v1/my-board-packs/${distributionId}/papers/${paper.documentId}/download`, {
    params: { version: paper.versionNo }, responseType: 'blob',
  })).data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = paper.fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** The reader reports a section or paper the recipient went to; fire-and-forget, never in the way of reading. */
export function recordReadEvent(distributionId: string, kind: 'SectionOpened' | 'PaperOpened', key: string) {
  void apiClient.post(`/api/v1/my-board-packs/${distributionId}/events`, { kind, key }).catch(() => undefined);
}

export interface SectionAttention { code: string; title: string; isPaper: boolean; readers: number; opens: number }
export interface RecipientReach { name: string; firstOpened: string | null; lastActivity: string | null; downloads: number; acknowledged: string | null }
export interface PackEngagement {
  versionNo: number;
  issuedDate: string | null;
  issued: number;
  opened: number;
  downloaded: number;
  acknowledged: number;
  /** Recipients who have not opened the pack, by name. */
  notOpened: string[];
  recipients: RecipientReach[];
  /** Each section and paper with how many recipients went to it; aggregate, never who. */
  attention: SectionAttention[];
  lastActivity: string | null;
}

export function usePackEngagement(boardPackId: string | undefined, versionNo?: number) {
  return useQuery({
    queryKey: ['boardPack', boardPackId, 'engagement', versionNo],
    queryFn: async () => (await apiClient.get<PackEngagement>(`/api/v1/boardpacks/${boardPackId}/engagement`, { params: { versionNo } })).data,
    enabled: Boolean(boardPackId),
    refetchInterval: 60_000,
  });
}

// ----------------------------- Issuing -----------------------------

export function useRecipientOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['boardPackRecipientOptions'],
    queryFn: async () =>
      (await apiClient.get<RecipientOption[]>('/api/v1/boardpacks/recipient-options')).data,
    enabled,
  });
}

/** Who a pack has been issued to, and whether they have opened it. */
export function useBoardPackDistributions(boardPackId: string | undefined) {
  return useQuery({
    queryKey: ['boardPack', boardPackId, 'distributions'],
    queryFn: async () =>
      (await apiClient.get<BoardPackDistribution[]>(`/api/v1/boardpacks/${boardPackId}/distributions`)).data,
    enabled: Boolean(boardPackId),
  });
}

export function useDistributeBoardPack(boardPackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ versionNo, recipientUserIds, note }:
      { versionNo: number; recipientUserIds: string[]; note: string | null }) =>
      (await apiClient.post<BoardPackDistribution[]>(
        `/api/v1/boardpacks/${boardPackId}/versions/${versionNo}/distribute`,
        { recipientUserIds, note })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boardPack', boardPackId, 'distributions'] }),
  });
}

/** Withdraws a recipient's access. The record is kept and marked, never deleted. */
export function useRevokeDistribution(boardPackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ distributionId, reason }: { distributionId: string; reason: string | null }) =>
      (await apiClient.post<BoardPackDistribution>(
        `/api/v1/boardpacks/${boardPackId}/distributions/${distributionId}/revoke`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boardPack', boardPackId, 'distributions'] }),
  });
}

// ----------------------------- The recipient's own list -----------------------------

/**
 * Board packs issued to the signed-in user. Requires no Board Pack permission — the distribution
 * record itself is the authorisation, so a board member sees exactly what was sent to them.
 */
export function useMyBoardPacks() {
  return useQuery({
    queryKey: ['myBoardPacks'],
    queryFn: async () => (await apiClient.get<MyBoardPack[]>('/api/v1/my-board-packs')).data,
  });
}

/** The recipient's own confirmation that they have read the pack. */
export function useAcknowledgeMyBoardPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (distributionId: string) =>
      (await apiClient.post<MyBoardPack>(`/api/v1/my-board-packs/${distributionId}/acknowledge`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['myBoardPacks'] }),
  });
}

const EXTENSION: Record<ReportFormat, string> = {
  Pdf: 'pdf', Word: 'docx', PowerPoint: 'pptx', Excel: 'xlsx', CompiledPdf: 'pdf', Csv: 'csv',
};

export async function downloadMyBoardPack(
  distributionId: string, title: string, versionNo: number, format: ReportFormat = 'CompiledPdf', annotated = false,
) {
  const blob = (await apiClient.get(`/api/v1/my-board-packs/${distributionId}/download`, {
    params: { format, annotated: annotated || undefined },
    responseType: 'blob',
  })).data as Blob;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title} v${versionNo}${annotated ? ' (annotated)' : ''}.${EXTENSION[format]}`;
  a.click();
  URL.revokeObjectURL(url);
}

/** The pack as the recipient reads it: the compiled PDF, papers inlined. */
export async function fetchMyBoardPackBlob(distributionId: string, format: ReportFormat = 'CompiledPdf', annotated = false): Promise<Blob> {
  return (await fetchMyBoardPackDocument(distributionId, format, annotated)).blob;
}

/** The bytes plus, for the compiled pack, where each section (by number) and paper (by code) begins. */
export async function fetchMyBoardPackDocument(
  distributionId: string, format: ReportFormat = 'CompiledPdf', annotated = false,
): Promise<{ blob: Blob; pages: Record<string, number> }> {
  // Read in the app: view-only papers stay in; a saved copy (downloadMyBoardPack) goes without them.
  const response = await apiClient.get(`/api/v1/my-board-packs/${distributionId}/download`, {
    params: { format, annotated: annotated || undefined, inline: true },
    responseType: 'blob',
  });
  const pages: Record<string, number> = {};
  const header = response.headers['x-pack-pages'] as string | undefined;
  if (header) {
    for (const part of header.split(',')) {
      const eq = part.lastIndexOf('=');
      if (eq > 0) pages[decodeURIComponent(part.slice(0, eq))] = Number(part.slice(eq + 1));
    }
  }
  return { blob: response.data as Blob, pages };
}

// ----------------------------- The reader's own place and bookmarks (ToR 1.0(xi), 2.2(iii)) -----------------------------

export interface PackBookmark { id: string; page: number; label: string; createdDate: string }

export function useMyBookmarks(distributionId: string | null) {
  return useQuery({
    queryKey: ['myBoardPacks', distributionId, 'bookmarks'],
    queryFn: async () => (await apiClient.get<PackBookmark[]>(`/api/v1/my-board-packs/${distributionId}/bookmarks`)).data,
    enabled: Boolean(distributionId),
  });
}

export function useMyBookmarkMutations(distributionId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['myBoardPacks', distributionId, 'bookmarks'] });
  const add = useMutation({ mutationFn: async (input: { page: number; label: string }) => (await apiClient.post<PackBookmark>(`/api/v1/my-board-packs/${distributionId}/bookmarks`, input)).data, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: async (id: string) => { await apiClient.delete(`/api/v1/my-board-packs/${distributionId}/bookmarks/${id}`); }, onSuccess: invalidate });
  return { add, remove };
}

/** Where the reader is, so the pack reopens there; sent quietly as they read. */
export async function saveReadingPosition(distributionId: string, page: number, key: string | null): Promise<void> {
  await apiClient.put(`/api/v1/my-board-packs/${distributionId}/reading-position`, { page, key });
}

export function parseReadingPosition(json: string | null): { page: number; key: string | null } | null {
  if (!json) return null;
  try { const p = JSON.parse(json) as { page?: number; key?: string | null }; return typeof p.page === 'number' && p.page > 0 ? { page: p.page, key: p.key ?? null } : null; } catch { return null; }
}
